import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { customerCategoriesService } from '../services/customerCategoriesService';
import { transformEntriesToMap } from '../utils/categoryEntries';
import { CustomerCategory } from '../types/customerCategories';
import { Users, AlertCircle } from 'lucide-react';
import DataCard, { CategoryCard } from '../components/common/DataCard';

const CustomerCategories: React.FC = () => {
  const { accessToken, setUserAndToken } = useUser();
  const [categories, setCategories] = useState<CustomerCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const data = await customerCategoriesService.getCustomerCategories(accessToken, setUserAndToken);
        // Sort by order_to_evaluate from entries
        const sortedData = data.sort((a, b) => {
          const aMap: any = transformEntriesToMap(a.entries);
          const bMap: any = transformEntriesToMap(b.entries);
          return (aMap.order_to_evaluate || 0) - (bMap.order_to_evaluate || 0);
        });
        setCategories(sortedData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch customer categories');
      } finally {
        setLoading(false);
      }
    };

    if (accessToken) {
      fetchCategories();
    }
  }, [accessToken, setUserAndToken]);

  // Editing UI removed; edits happen in CategoryDetails page


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading customer categories...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Categories</h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Edit Form Modal removed; edits take place in CategoryDetails */}

      {/* Coming Soon Modal removed for now */}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg text-slate-900 mb-1 px-2">Manage customer categorization rules and criteria</h2>
        </div>
        <Link
          to="/customer-categories/create"
          className="bg-[#4a67b7] hover:bg-primary/90 text-white px-6 py-2 rounded-lg flex items-center shadow transition"
        >
          + Add Category
        </Link>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-lg text-slate-900">All Risk Profiles</h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-slate-500">Total Categories:</span>
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
              {categories.length}
            </span>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {categories.map((category) => (
              <CategoryCard key={category.id} category={category} />
            ))}
          </div>
        </div>
      </div>

      {/* Empty State */}
      {categories.length === 0 && !loading && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Categories Found</h3>
            <p className="text-slate-600">No customer categories are currently configured.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerCategories;

